import { forwardRef, useState, useEffect, type ReactNode, type Ref } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { resolveSurfaceStyle } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../_internal/factory.js';
import './Avatar.css';

export interface AvatarProps extends BaseProps {
  src?: string | null;
  alt?: string;
  name?: string;
  /** Avatar thumbnail tier. */
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  /** circle (default) or rounded square. */
  shape?: 'circle' | 'square';
  children?: ReactNode;
}

function initialsFromName(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] ?? '').concat(parts[1]?.[0] ?? '').toUpperCase() || '?';
}

function resolveAvatarSize(size: AvatarProps['size']): string {
  if (size === undefined) return 'var(--rr-thumb-md)';
  if (typeof size === 'number') return `${size}px`;
  if (['xxs', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl'].includes(size as string)) {
    return `var(--rr-thumb-${size})`;
  }
  return size as string;
}

/** Square or round avatar with image fallback to initials. */
export const Avatar = forwardRef(function Avatar(
  { src, alt, name, size = 'md', shape = 'circle', children, surface, ...rest }: AvatarProps,
  ref: Ref<HTMLSpanElement>
): ReactNode {
  const [errored, setErrored] = useState(false);
  useEffect(() => setErrored(false), [src]);

  const base = useBaseAttrs('rr-avatar', rest, [`rr-avatar--${shape}`]);
  const { dom } = splitBaseProps(rest);
  const dim = resolveAvatarSize(size);
  const fallbackSurface = surface ?? (name ? { color: stringToColor(name) } : undefined);
  const fallbackStyle = resolveSurfaceStyle(fallbackSurface);
  const hasImage = src && !errored;

  return (
    <span {...dom}
      ref={ref}
      {...base.attrs}
      className={base.className}
      style={{ ...base.style, ...fallbackStyle, width: dim, height: dim, fontSize: `calc(${dim} * 0.4)` }}
    >
      {hasImage ? (
        <img className="rr-avatar__img" src={src ?? undefined} alt={alt ?? name ?? ''} onError={() => setErrored(true)} />
      ) : children ? (
        <span className="rr-avatar__content">{children}</span>
      ) : (
        <span className="rr-avatar__initials">{initialsFromName(name)}</span>
      )}
    </span>
  );
});

export interface AvatarGroupProps extends BaseProps {
  children?: ReactNode;
  spacing?: number;
  max?: number;
  total?: number;
}

/** Overlapping avatar cluster. */
export function AvatarGroup({ children, spacing = -8, max, total, ...rest }: AvatarGroupProps): ReactNode {
  const arr = Array.isArray(children) ? children : [children];
  const visible = max ? arr.slice(0, max) : arr;
  const extra = total ? total - visible.length : (max && arr.length > max ? arr.length - max : 0);
  const base = useBaseAttrs('rr-avatar-group', rest);
  const { dom } = splitBaseProps(rest);
  return (
    <span {...dom} {...base.attrs} className={base.className} style={{ ...base.style, ['--rr-avatar-spacing' as string]: `${spacing}px` }}>
      {visible}
      {extra > 0 ? (
        <span className="rr-avatar rr-avatar--circle rr-avatar--more" style={{ width: 'var(--rr-thumb-md)', height: 'var(--rr-thumb-md)' }}>
          +{extra}
        </span>
      ) : null}
    </span>
  );
}

function stringToColor(s: string): string {
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0;
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 65%, 55%)`;
}
